import { type DocumentV1Payload } from '@taxfyle/documents-api-client'
import {
  type DocumentLinkResource,
  type DocumentResource,
  type DocumentRejectionResource,
  type DocumentStageResource,
  type ManualAttestationReasonResource,
  type PreprocessingPhaseResource,
  type SearchDocumentsRequest,
} from '@taxfyle/documents-api-client/v5'
import { Id } from '@/infra/id'
import { match } from '@/utils/pattern-match-util'
import { maybeParseDate } from '@/utils/date-util'
import type { DocumentProps } from './Document'
import { type DocumentLinks } from './DocumentLinks'
import { type DocumentArchival, DocumentRetention } from './DocumentRetention'
import {
  type DocumentRejection,
  type DocumentStage,
  type ManualAttestationReason,
  type PreprocessingPhase,
} from './DocumentStage'
import type {
  AdminDocumentsQuery,
  DocumentsSortField,
  JobDocumentsQuery,
  SearchDocumentsQuery,
  UploadDocumentOptions,
  UserDocumentsQuery,
} from './DocumentStore'

/**
 * Maps a v5 document resource to document props, except the models it
 * refers to.
 */
export function mapDocumentV5ResourceToProps(
  resource: DocumentResource,
): Partial<DocumentProps> {
  return {
    id: Id(resource.id),
    title: resource.title,
    workspaceId: Id(resource.workspace_id),
    archived: resource.archived,
    // The v5 API has no edit permission concept so true by default.
    canEdit: true,
    dateCreated: maybeParseDate(resource.date_created),
    dateModified: maybeParseDate(resource.date_modified),
    stage: mapResourceToDocumentStage(resource),
    retention: mapResourceToDocumentRetention(resource),
    links: mapResourceToDocumentLinks(resource.links),
  }
}

/**
 * Maps a v1 realtime document payload to document props, except the models
 * it refers to.
 */
export function mapDocumentV1PayloadToProps(
  payload: DocumentV1Payload,
): Partial<DocumentProps> {
  return {
    id: Id(payload.id),
    title: payload.title,
    workspaceId: Id(payload.workspace_id),
    archived: payload.archived,
    canEdit: true,
    dateCreated: maybeParseDate(payload.date_created),
    dateModified: maybeParseDate(payload.date_modified),
    stage: mapResourceToDocumentStage(payload),
    retention: mapResourceToDocumentRetention(payload),
    links: {
      job: { jobIds: payload.links.job.job_ids.map((id) => Id(id)) },
      workRequest: {
        workRequestId: payload.links.work_request.work_request_id
          ? Id(payload.links.work_request.work_request_id)
          : null,
      },
      strategy: {
        folderId: payload.links.strategy.folder_id
          ? Id(payload.links.strategy.folder_id)
          : null,
      },
    },
    description: payload.description,
  }
}

/**
 * Maps an upload request to the props of the document the v5 API creates
 * for it, except the models it refers to.
 */
export function mapUploadRequestToDocumentV5Props(
  request: UploadDocumentOptions,
  documentId: Id,
  now: Date,
): Partial<DocumentProps> {
  return {
    id: documentId,
    title: request.file.name,
    workspaceId: request.workspaceId,
    dateCreated: now,
    dateModified: now,
    links: {
      job: { jobIds: [Id.assert(request.jobId)] },
      workRequest: { workRequestId: null },
      strategy: { folderId: null },
    },
  }
}

/**
 * Maps a documents sort field to the v5 search order.
 */
function mapDocumentsSortFieldToOrderBy(
  field: DocumentsSortField,
): SearchDocumentsRequest['order_by'] {
  return match<DocumentsSortField, SearchDocumentsRequest['order_by']>(field, {
    Title: 'title',
    DateCreated: 'date_created',
    DateModified: 'date_modified',
  })
}

/**
 * Maps a search documents query to the search documents request.
 */
export function mapSearchDocumentsQueryToSearchRequest(
  query: SearchDocumentsQuery,
): SearchDocumentsRequest {
  const base = {
    order_by: query.orderBy
      ? mapDocumentsSortFieldToOrderBy(query.orderBy.field)
      : 'date_created',
    order: query.orderBy?.direction ?? 'desc',
    limit: query.limit,
    after: query.after ?? undefined,
    archived: query.archived,
  }

  return match<SearchDocumentsQuery['context'], SearchDocumentsRequest>(
    query.context,
    {
      Job: () => {
        const jobQuery = query as JobDocumentsQuery
        return {
          ...base,
          context: 'JOB',
          job_id: Id.assert(jobQuery.jobId),
          ...cleanFilters({
            workspace_id: jobQuery.workspaceId,
            search_text: jobQuery.searchText || undefined,
            document_type_ids: jobQuery.documentTypeIds?.map(Id.assert),
          }),
        }
      },
      User: () => {
        const userQuery = query as UserDocumentsQuery
        return {
          ...base,
          context: 'USER',
          ...cleanFilters({ workspace_id: userQuery.workspaceId }),
        }
      },
      Admin: () => {
        const adminQuery = query as AdminDocumentsQuery
        return {
          ...base,
          context: 'ADMIN',
          ...cleanFilters({
            search_text: adminQuery.searchText || undefined,
            owner_public_ids: adminQuery.ownerPublicIds?.map(Id.assert),
            workspace_ids: adminQuery.workspaceIds?.map(Id.assert),
            job_ids: adminQuery.jobIds?.map(Id.assert),
            document_type_ids: adminQuery.documentTypeIds?.map(Id.assert),
          }),
        }
      },
    },
  )
}

/**
 * Removes undefined values and empty arrays from the filters, so they
 * don't end up in the request's query parameters.
 */
function cleanFilters<T extends object>(filters: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(filters).filter(
      ([, value]) =>
        value !== undefined && (!Array.isArray(value) || value.length > 0),
    ),
  ) as Partial<T>
}

/**
 * The stage fields shared by the document resource and the v1
 * realtime payload.
 */
interface StageFields {
  stage: DocumentStageResource
  preprocessing_phase: PreprocessingPhaseResource | null
  rejection: DocumentRejectionResource | null
  manual_attestation_reason: ManualAttestationReasonResource | null
}

/**
 * The retention fields shared by the document resource and the v1
 * realtime payload.
 */
interface RetentionFields {
  archived: boolean
  date_archived: string | null
  archived_by_user_public_id: string | null
  purged: boolean
}

/**
 * Maps the stage fields of a document resource to the document stage.
 */
export function mapResourceToDocumentStage(
  resource: StageFields,
): DocumentStage {
  return match<DocumentStageResource, DocumentStage>(resource.stage, {
    PREPROCESSING: () => ({
      type: 'Preprocessing',
      phase: mapPreprocessingPhaseResourceToPreprocessingPhase(
        resource.preprocessing_phase,
      ),
    }),
    ATTESTATION_SCREENING: () => ({
      type: 'AttestationScreening',
    }),
    MANUAL_ATTESTATION_REQUIRED: () => ({
      type: 'ManualAttestationRequired',
      reason: mapManualAttestationReasonResourceToManualAttestationReason(
        resource.manual_attestation_reason,
      ),
    }),
    RELEASED: () => ({ type: 'Released' }),
    REJECTED: () => ({
      type: 'Rejected',
      rejection: mapDocumentRejectionResourceToProps(resource.rejection),
    }),
  })
}

/**
 * Maps a document links resource to the document links.
 */
export function mapResourceToDocumentLinks(
  links: DocumentLinkResource,
): DocumentLinks {
  return {
    job: { jobIds: links.job.job_ids.map((id) => Id(id)) },
    workRequest: {
      workRequestId: links.work_request
        ? Id(links.work_request.work_request_id)
        : null,
    },
    strategy: {
      folderId: links.strategy ? Id(links.strategy.folder_id) : null,
    },
  }
}

/**
 * Maps the retention fields of a document resource to the
 * document retention.
 */
export function mapResourceToDocumentRetention(
  resource: RetentionFields,
): DocumentRetention {
  const archival = mapDocumentArchivalResourceToProps(resource)

  if (resource.purged) {
    // Purge details are not exposed by the API.
    return { type: 'Purged', archival, purge: null }
  }

  if (resource.archived) {
    return { type: 'Archived', archival }
  }

  return DocumentRetention.active
}

/**
 * Maps the archival fields of a document resource to the
 * archival information.
 */
function mapDocumentArchivalResourceToProps(
  resource: RetentionFields,
): DocumentArchival | null {
  if (!resource.date_archived || !resource.archived_by_user_public_id) {
    return null
  }

  return {
    archivedAt: new Date(resource.date_archived),
    archivedBy: Id(resource.archived_by_user_public_id),
  }
}

/**
 * Maps a preprocessing phase resource to the document preprocessing phase.
 */
function mapPreprocessingPhaseResourceToPreprocessingPhase(
  phase: PreprocessingPhaseResource | null,
): PreprocessingPhase {
  if (!phase) {
    return 'VirusScanning'
  }

  return match<PreprocessingPhaseResource, PreprocessingPhase>(phase, {
    CHECKING_FOR_FILE_CONVERSION: () => 'CheckingForFileConversion',
    CONVERTING_FILE: () => 'ConvertingFile',
    FINALIZING: () => 'Finalizing',
    VIRUS_SCANNING: () => 'VirusScanning',
  })
}

/**
 * Maps a manual attestation reason resource to the document reason.
 */
export function mapManualAttestationReasonResourceToManualAttestationReason(
  reason: ManualAttestationReasonResource | null,
): ManualAttestationReason | undefined {
  if (!reason) {
    return undefined
  }

  return match<ManualAttestationReasonResource, ManualAttestationReason>(
    reason,
    {
      CLIENT_NAME_MISMATCH: () => 'ClientNameMismatch',
      MISSING_PRO_SIGNATURE: () => 'MissingProSignature',
      EXTRACTION_FAULTED: () => 'ExtractionFaulted',
      ATTESTATION_FAULTED: () => 'AttestationFaulted',
      TIMED_OUT: () => 'TimedOut',
    },
  )
}

/**
 * Maps a document rejection resource to the document rejection.
 */
function mapDocumentRejectionResourceToProps(
  rejection: DocumentRejectionResource | null,
): DocumentRejection {
  if (!rejection) {
    return 'ConversionFailed'
  }

  return match<DocumentRejectionResource, DocumentRejection>(rejection, {
    QUARANTINED: 'Quarantined',
    CONVERSION_FAILED: 'ConversionFailed',
  })
}
